import { FormEvent, useEffect, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { api, ApiError, SubscriptionRecord, TenantWorkspace } from "../../lib/api.js";
import EmptyState from "../../components/EmptyState.js";
import Drawer from "../../components/Drawer.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

export default function AdminBilling() {
  const [workspaces, setWorkspaces] = useState<TenantWorkspace[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionRecord[]>([]);
  const [providers, setProviders] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [refund, setRefund] = useState<{ subscriptionId: string; providerName: string; reason: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.workspace(),
      api.subscription(),
      api.paymentMethods().catch(() => ({ methods: [] }))
    ])
      .then(([w, s, m]) => {
        setWorkspaces(w.workspaces ?? []);
        setSubscriptions(s.subscriptions ?? []);
        setProviders(m.methods ?? []);
      })
      .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  const onRefundSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!refund) return;
    if (!refund.subscriptionId || !refund.providerName) {
      setErr("Subscription id and provider are required.");
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const r = await api.refundPayment({
        subscriptionId: refund.subscriptionId,
        providerName: refund.providerName,
        reason: refund.reason || undefined
      });
      setBanner(r.success
        ? `Refund issued for ${refund.subscriptionId}.`
        : r.manualActionRequired
        ? `Refund flagged for manual action on ${refund.subscriptionId}.`
        : `Refund attempt recorded: ${r.message ?? "(no message)"}`);
      setRefund(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const tSubs = useTableState(subscriptions as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "workspaceId", "planId", "status", "paymentMethodLabel", "billingEmail"],
    initialPageSize: 25
  });

  const workspaceName = (id: string) =>
    (workspaces.find((w) => w.id === id) as { workspaceName?: string; name?: string } | undefined)?.workspaceName ??
    workspaces.find((w) => w.id === id)?.name ?? id;

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Billing & refunds</h1>
          <p>All active subscriptions across tenants. Issue refunds by subscription id and provider.</p>
        </div>
        <button className="btn btn-primary" type="button" onClick={() => setRefund({ subscriptionId: "", providerName: providers[0] ?? "", reason: "" })}>
          Issue refund
        </button>
      </header>

      {err && <div className="alert alert-error">{err}</div>}
      {banner && <div className="alert alert-ok">{banner}</div>}

      {loading ? <SkeletonTable rows={5} columns={4} /> :
       subscriptions.length === 0 ? (
         <EmptyState
           icon="💳"
           title="No subscriptions yet"
           description="Subscriptions appear when a workspace owner picks a plan during signup, or when a platform admin assigns one from a workspace's billing tab."
         />
       ) : (
        <div>
          <TableToolbar
            search={tSubs.search}
            setSearch={tSubs.setSearch}
            page={tSubs.page}
            setPage={tSubs.setPage}
            pageSize={tSubs.pageSize}
            setPageSize={tSubs.setPageSize}
            totalPages={tSubs.totalPages}
            totalRows={tSubs.totalRows}
            hideExport
            searchPlaceholder="Search subscription / workspace / plan…"
          />
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Subscription</th>
                <th>Workspace</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Payment method</th>
                <th>Billing email</th>
                <th>Renewal</th>
              </tr>
            </thead>
            <tbody>
              {(tSubs.paged as unknown as SubscriptionRecord[]).map((s) => (
                <tr key={s.id}>
                  <td><code>{s.id}</code></td>
                  <td>{workspaceName(s.workspaceId)}</td>
                  <td>{s.planId}</td>
                  <td>
                    {s.status === "active" ? <span className="pill pill-ok">Active</span> :
                     s.status === "trialing" ? <span className="pill pill-info">Trialing</span> :
                     s.status === "grace-period" ? <span className="pill pill-warn">Grace</span> :
                     <span className="pill pill-err">Past due</span>}
                  </td>
                  <td className="soft">{s.paymentMethodLabel}</td>
                  <td className="soft">{s.billingEmail}</td>
                  <td className="soft">{s.renewalDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
       )}

      <Drawer
        open={refund != null}
        title="Issue refund"
        onClose={() => setRefund(null)}
        width={520}
        asForm
        onSubmit={onRefundSubmit}
        footer={(
          <>
            <button type="button" className="btn" onClick={() => setRefund(null)}>Cancel</button>
            <button type="submit" className="btn btn-danger" disabled={submitting}>
              {submitting ? "Submitting…" : "Issue refund"}
            </button>
          </>
        )}
      >
        {refund && (
          <>
            <div className="field">
              <label>Subscription / payment reference</label>
              <input
                type="text"
                required
                value={refund.subscriptionId}
                onChange={(e) => setRefund({ ...refund, subscriptionId: e.target.value })}
                placeholder="sub_... or provider reference"
              />
            </div>
            <div className="field">
              <label>Provider</label>
              <select
                required
                value={refund.providerName}
                onChange={(e) => setRefund({ ...refund, providerName: e.target.value })}
              >
                <option value="">— select —</option>
                {providers.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              {providers.length === 0 && <small className="muted">No active providers. Configure one in Payment gateways first.</small>}
            </div>
            <div className="field">
              <label>Reason (optional)</label>
              <textarea rows={2} value={refund.reason} onChange={(e) => setRefund({ ...refund, reason: e.target.value })} />
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}
